import { useEffect, useState } from 'react';
import { RestartAlt, Check, Close, EditRounded } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Popover,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import PropTypes from 'prop-types';
import styles from './styles.js';
import { useDispatch } from 'react-redux';
import { enqueueSnackbar } from '../../../store/actions/snackbar.js';

const ChartsAxisPopover = ({ title, onSubmit, onReset, defaultMin = '', defaultMax = '' }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [minValue, setMinValue] = useState(defaultMin);
  const [maxValue, setMaxValue] = useState(defaultMax);
  const classes = styles();
  const open = Boolean(anchorEl);
  const id = open ? 'axis-range-popover' : undefined;
  const dispatch = useDispatch();

  useEffect(() => {
    setMinValue(defaultMin);
    setMaxValue(defaultMax);
  }, [defaultMin, defaultMax]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    const min = parseFloat(minValue);
    const max = parseFloat(maxValue);

    if (!isNaN(min) && !isNaN(max) && max <= min) {
      dispatch(
        enqueueSnackbar(
          'Max value must be greater than Min value',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return;
    }

    onSubmit?.(minValue, maxValue);
    handleClose();
  };

  const handleReset = () => {
    setMinValue(defaultMin);
    setMaxValue(defaultMax);
    onReset();
  };

  const handleCancel = () => {
    handleClose();
  };

  const handleInputChange = (setter) => (event) => {
    setter(event.target.value);
  };

  return (
    <Box>
      <Tooltip title={`Edit ${title} Range`}>
        <IconButton onClick={handleOpen}>
          <EditRounded sx={{ fontSize: '15px' }}/>
        </IconButton>
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box p={2}>
          <Box display='flex' justifyContent='space-between'>
          <Typography variant="subtitle1">{title}</Typography>
          <Tooltip title="Cancel">
              <IconButton onClick={handleCancel}>
                <Close />
              </IconButton>
            </Tooltip>
          </Box>
          <Box display="flex" alignItems="center" gap={2} mt={1}>
            <TextField
              className={classes.inputField}
              size="small"
              label="Min"
              type='Number'
              value={
                Number(minValue).toString().split('.')[1]?.length > 2
                  ? Number(minValue).toFixed(2)
                  : minValue
            }
              onChange={handleInputChange(setMinValue)}
              InputProps={{
                style: {
                  color: minValue === defaultMin ? 'gray' : 'black',
                },
              }}
            />
            <TextField
              className={classes.inputField}
              size="small"
              label="Max"
              type='Number'
              value={
                 Number(maxValue).toString().split('.')[1]?.length > 2
                   ? Number(maxValue).toFixed(2)
                   : maxValue
            }
              onChange={handleInputChange(setMaxValue)}
              InputProps={{
                style: {
                  color: maxValue === defaultMax ? 'gray' : 'black',
                },
              }}
            />

            <Tooltip title="Apply">
              <IconButton onClick={handleApply}>
                <Check />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset to defaults">
              <IconButton onClick={handleReset}>
                <RestartAlt />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

ChartsAxisPopover.propTypes = {
  title: PropTypes.string.isRequired,
  onSubmit: PropTypes.func,
  onReset: PropTypes.func,
  defaultMin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultMax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ChartsAxisPopover;
